// MSAL configuration for Microsoft Entra ID direct auth (Authorization Code + PKCE via
// @azure/msal-browser + @azure/msal-react) — see docs/adr-001-shared-cognito-identity.md
// (superseded; now records the full-Entra-direct identity decision).
//
// Every identifier below is read from Vite env (import.meta.env) — nothing here is a real
// tenant id, client id, or hostname. See apps/web/.env.example for the placeholder shape.
//
// Three run modes (see src/lib/mock.ts / src/lib/localAuth.ts):
//   - `dev:mock`  (VITE_MOCK=1)       — no auth at all, in-memory fixture data.
//   - local dev-auth (VITE_LOCAL_AUTH=1) — no real Entra app registration needed; pairs with
//     the backend's SCRIBE_DEV_AUTH=1 bypass. Skips MSAL entirely and sends a fixed dev
//     bearer credential the backend dev-auth path recognizes as "any non-empty token".
//   - real Entra sign-in (default)   — full MSAL Authorization Code + PKCE flow.
import type { Configuration } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';
import { isMock } from './mock';
import { isLocalAuth } from './localAuth';

function required(name: keyof ImportMetaEnv, value: string | undefined): string {
  if (!value) {
    if (isMock() || isLocalAuth()) {
      // Mock/local-auth modes never talk to Entra, but MSAL still validates its config
      // shape at construction (an empty clientId throws before the mock check can run —
      // exactly what happens in CI, where the gitignored .env.mock doesn't exist). Return
      // an obviously-fake, syntactically valid placeholder instead of ''.
      return 'scribe-fake-auth-placeholder';
    }
    // Fail loudly in real (non-mock, non-local-dev-auth) builds — a missing auth config
    // value must never silently fall through to an unauthenticated or misconfigured client.
    console.error(`[scribe-web] missing required env var ${String(name)}`);
  }
  return value ?? '';
}

const tenantId = required('VITE_ENTRA_TENANT_ID', import.meta.env.VITE_ENTRA_TENANT_ID);
const clientId = required('VITE_ENTRA_CLIENT_ID', import.meta.env.VITE_ENTRA_CLIENT_ID);
const authority =
  import.meta.env.VITE_ENTRA_AUTHORITY ||
  (tenantId ? `https://login.microsoftonline.com/${tenantId}` : '');

/** The Scribe API's exposed scope (NOT a Microsoft Graph scope) — e.g.
 * `api://<scribe-api-app-id>/access_as_user`. This is what gets acquired as the access
 * token attached as the Bearer credential on Scribe API calls. */
export const apiScope = required('VITE_ENTRA_API_SCOPE', import.meta.env.VITE_ENTRA_API_SCOPE);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin + '/auth/callback',
    postLogoutRedirectUri:
      import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || window.location.origin + '/',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage (not localStorage) so signing out of the browser tab clears state,
    // matching the previous Cognito-via-oidc-client-ts behavior.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // never log PII-bearing MSAL diagnostic messages
        if (level === LogLevel.Error) {
          console.error(`[msal] ${message}`);
        }
      },
    },
  },
};

/** Login request: OIDC scopes only (openid/profile/email) — the API scope is acquired
 * separately via acquireTokenSilent so the two can be requested/consented independently
 * and the sign-in flow doesn't fail just because API scope consent is pending. */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/** Token request for the Scribe API access token (never a Microsoft Graph scope) —
 * attached as the Bearer credential by src/lib/apiClient.ts. */
export const apiTokenRequest = {
  scopes: [apiScope],
};

/** Delegated Microsoft Graph scope for org-wide people search in the share picker —
 * acquired SEPARATELY from the Scribe API scope (MSAL cannot mix resources in one
 * acquireTokenSilent call). User-consentable by Entra default; tenants that disable
 * user consent need the admin grant in docs/entra-setup-walkthrough.md Step 2. */
export const graphPeopleSearchRequest = {
  scopes: ['User.ReadBasic.All'],
};
